import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {EliteConfig} from "crqlar-react-common/dist/models/integrations/elite/eliteConfig.model";
import {EliteService} from "crqlar-react-common/dist/services/integrations/elite/elite.service";
import {Box, Divider, Stack, Tab, Tabs} from "@mui/material";
import Typography from "@mui/material/Typography";
import Logs from "./Logs";
import EliteConfigForm from "./EliteConfigForm";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";

const EliteConfigDetails = () => {

    useTrackScreenVisit('system elite config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const eliteConfigData = useEntity<EliteConfig>({
        get: async () => {
            return await EliteService.find()
        },
        create: EliteService.create,
        update: EliteService.update,
        delete: (id) => EliteService.delete({id: [id]}),
        isValid: (eliteConfig) => {
            return !!eliteConfig.name
        },
        cleanupBeforeSave: (eliteConfig) => {
            return {
                ...eliteConfig,
                name: eliteConfig.name?.trim()
            }
        }
    })

    const eliteConfig = eliteConfigData.item

    const handleCreate = () => {
        eliteConfigData.startCreate({
            enabled: true,
            processingEnabled: true,
            otaMappingRules: {
                autoCreateResourceIfNotFound: true,
                pickAdditionalResourceInfo: true,
                autoCreateServiceForRatePlanIfNotFound: true
            }
        } as EliteConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={eliteConfig.name}
            data={eliteConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={eliteConfigData.saving}
                onDelete={() => eliteConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {eliteConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    gap: 6,
                }}
            >
                {
                    eliteConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!eliteConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Stats and Logs')} value={'logs'}/>
                    </Tabs>
                }

                {
                    !eliteConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <EliteConfigForm eliteConfigData={eliteConfigData}/>

                            {
                                !!eliteConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs eliteConfigData={eliteConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {eliteConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default EliteConfigDetails;
